// Display helpers for tasks (phase 2): status labels, due dates, how long the turn has been
// waiting. Dates are local (Europe/Paris for the team).
import type { DisplayStatus, Message, TaskInfo, UserRef } from './api';

export const STATUS_LABEL: Record<DisplayStatus, string> = {
  todo: 'À faire',
  doing: 'En cours',
  waiting: 'En attente',
  done: 'Terminé',
};

const firstName = (u: UserRef) => u.display_name.split(' ')[0] ?? u.display_name;

/** « En attente de Thomas », « À faire »… */
export function statusText(task: TaskInfo, full = false): string {
  if (task.display_status === 'waiting' && task.waiting_on) {
    return `En attente de ${full ? task.waiting_on.display_name : firstName(task.waiting_on)}`;
  }
  return STATUS_LABEL[task.display_status];
}

export const localIso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const todayIso = () => localIso(new Date());

function dateOf(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

/** « jeu. 5 nov. » */
export function shortDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }).format(dateOf(iso));
}

/** « jeudi 5 novembre » */
export function longDate(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }).format(dateOf(iso));
}

export function isOverdue(task: TaskInfo): boolean {
  return task.status !== 'done' && !!task.due_date && task.due_date < todayIso();
}

/** The due date as shown on a card: « En retard · lun. 2 nov. », « Aujourd'hui », « Sans échéance ». */
export function dueText(task: TaskInfo): string {
  if (!task.due_date) return 'Sans échéance';
  if (isOverdue(task)) return `En retard · ${shortDate(task.due_date)}`;
  if (task.due_date === todayIso()) return "Aujourd'hui";
  return shortDate(task.due_date);
}

/** Working days (Monday to Friday) between a time and now. */
export function businessDaysSince(iso: string, now = new Date()): number {
  const start = new Date(iso);
  let days = 0;
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  while (d < end) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) days++;
  }
  return days;
}

/** « depuis hier », « depuis 3 jours », « depuis aujourd'hui ». */
export function sinceText(iso: string | null): string {
  if (!iso) return '';
  const start = new Date(iso);
  const now = new Date();
  const days = Math.round(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
      new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime()) /
      86_400_000,
  );
  if (days <= 0) return "depuis aujourd'hui";
  if (days === 1) return 'depuis hier';
  return `depuis ${days} jours`;
}

/** « aujourd'hui », « hier », « lun. 2 nov. »: when a task was finished. */
export function doneText(iso: string): string {
  const day = localIso(new Date(iso));
  const today = todayIso();
  const yesterday = localIso(new Date(Date.now() - 86_400_000));
  if (day === today) return "aujourd'hui";
  if (day === yesterday) return 'hier';
  return shortDate(day);
}

/** The other role, proposed for « Passer la main » (the requester if I am the assignee…). */
export function handoffTarget(task: TaskInfo, me: string): UserRef | null {
  if (task.assignee && task.assignee.id !== me && task.waiting_on?.id !== task.assignee.id) return task.assignee;
  if (task.requester.id !== me && task.waiting_on?.id !== task.requester.id) return task.requester;
  if (task.assignee && task.assignee.id !== me) return task.assignee;
  return task.requester.id !== me ? task.requester : null;
}

/** Group of « À moi d'agir » by due date. */
export function dueGroup(task: TaskInfo): 'late' | 'today' | 'week' | 'later' | 'none' {
  if (!task.due_date) return 'none';
  const today = todayIso();
  if (task.due_date < today) return 'late';
  if (task.due_date === today) return 'today';
  const now = new Date();
  const sunday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + ((7 - now.getDay()) % 7));
  return task.due_date <= localIso(sunday) ? 'week' : 'later';
}

export const isTask = (m: Message): m is Message & { task: TaskInfo } => m.kind === 'task' && !!m.task;
